import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { partnerApi } from "../../../services/partnerApi";
import OnboardingStepFrame from "../../../components/instructor/OnboardingStepFrame";

export default function PartnerReviewStep() {
  const { partner, refreshPartner } = useAuth();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  const org = partner?.org;
  const completeness = partner?.completeness;
  const sources = partner?.sources || [];

  async function handleSubmit() {
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      const latest = await refreshPartner();
      if (latest?.completeness && !latest.completeness.canSubmit) {
        const items = (latest.completeness.requiredMissing || [])
          .map((m) => m.label)
          .join("; ");
        setError(
          items
            ? `Application incomplete (${latest.completeness.score}%): ${items}`
            : `Application incomplete (${latest.completeness.score}%). Finish required steps first.`
        );
        return;
      }
      const wasRejected = org?.status === "Rejected";
      await partnerApi.submit();
      await refreshPartner();
      setSuccess(
        wasRejected
          ? "Application resubmitted. Our team will set up programs from your website, then review."
          : "Application submitted. Our team will set up programs from your website, then review."
      );
    } catch (err) {
      let message = err.message || "Submit failed";
      if (err.data?.completeness?.requiredMissing?.length) {
        const items = err.data.completeness.requiredMissing.map((m) => m.label).join("; ");
        message = `${message} — ${items}`;
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleWithdraw() {
    setError("");
    setSuccess("");
    setWithdrawing(true);
    try {
      await partnerApi.withdraw();
      await refreshPartner();
      setSuccess("Application withdrawn. You can edit and resubmit.");
    } catch (err) {
      setError(err.message || "Could not withdraw");
    } finally {
      setWithdrawing(false);
    }
  }

  if (!org) {
    return <div className="text-gray-500">Loading…</div>;
  }

  return (
    <OnboardingStepFrame
      title="Review & submit"
      subtitle="Confirm your details. After submit, developers list programs from your website — you do not enter course catalogs here."
    >
      <div className="space-y-5">
        {error && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}
        {success && (
          <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {success}
          </div>
        )}

        <div className="space-y-3 rounded-2xl border border-emerald-100 bg-white/90 p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-semibold text-gray-900">
              {org.trade_name || org.legal_name || "Your organization"}
            </h2>
            <span className="text-sm text-gray-500">{completeness?.score ?? 0}% complete</span>
          </div>
          <p className="text-sm text-gray-600">
            {(() => {
              const locs = Array.isArray(org.service_locations) ? org.service_locations : [];
              if (locs.length) {
                return locs
                  .map((loc) => [loc.city, loc.state, loc.country].filter(Boolean).join(", "))
                  .join(" · ");
              }
              return [org.city, org.state, org.country].filter(Boolean).join(", ");
            })()}
          </p>
          <p className="line-clamp-4 whitespace-pre-wrap text-sm text-gray-700">{org.about}</p>
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-gray-500">Website</dt>
              <dd className="break-all text-emerald-800">
                {sources[0]?.website_url || org.website || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Logo</dt>
              <dd>{org.logo_url ? "Uploaded" : "Not uploaded (optional)"}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Gallery</dt>
              <dd>
                {(partner?.assets || []).filter((a) => a.kind === "gallery" && a.public_url)
                  .length
                  ? `${(partner?.assets || []).filter((a) => a.kind === "gallery" && a.public_url).length} photo(s)`
                  : "Missing"}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Cancellation policy</dt>
              <dd>{org.cancellation_policy ? "Provided" : "Missing"}</dd>
            </div>
          </dl>
        </div>

        {completeness?.missing?.length > 0 && (
          <ul className="space-y-2 text-sm">
            {completeness.missing.map((item) => (
              <li key={item.key} className="flex items-center justify-between">
                <span className={item.optional ? "text-gray-500" : "text-gray-800"}>
                  {item.label}
                  {item.optional ? " (optional)" : ""}
                </span>
                <Link
                  to={`/partner/onboarding/${item.step}`}
                  className="font-medium text-emerald-700 hover:underline"
                >
                  Fix
                </Link>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap gap-3">
          <Link
            to="/partner/onboarding/commercial"
            className="rounded-lg border border-emerald-200 px-4 py-2.5 text-sm font-medium text-emerald-800 hover:bg-emerald-50"
          >
            Back
          </Link>
          {["Draft", "Rejected"].includes(org.status) && (
            <button
              type="button"
              disabled={submitting || !completeness?.canSubmit}
              onClick={handleSubmit}
              className="rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit application"}
            </button>
          )}
          {["Submitted", "UnderReview"].includes(org.status) && (
            <button
              type="button"
              disabled={withdrawing}
              onClick={handleWithdraw}
              className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-900 disabled:opacity-60"
            >
              {withdrawing ? "Withdrawing…" : "Withdraw to edit"}
            </button>
          )}
          <Link to="/partner" className="rounded-lg px-4 py-2.5 text-sm text-gray-600 hover:underline">
            Dashboard
          </Link>
        </div>
      </div>
    </OnboardingStepFrame>
  );
}
